/** Stored layout coordinate units (same as DB / GraphQL), not CSS or zoom-scaled pixels. */
export const TABLE_LAYOUT_SHAPE_EPSILON = 1.0;

/** Side length for square/circle tables and the fixed short side for default rectangles (matches a standard 4-top like “Table 1”). */
export const TABLE_LAYOUT_BASE_DIMENSION = 120;

export type TableLayoutShape = "CIRCLE" | "SQUARE" | "RECTANGLE";

const API_SHAPES = new Set<string>(["CIRCLE", "SQUARE", "RECTANGLE"]);

export function parseApiLayoutShape(raw: string | null | undefined): TableLayoutShape | null {
  if (raw == null || raw === "") return null;
  return API_SHAPES.has(raw) ? (raw as TableLayoutShape) : null;
}

export function resolveLayoutShape(
  apiShape: string | null | undefined,
  width: number,
  height: number
): TableLayoutShape {
  const parsed = parseApiLayoutShape(apiShape);
  if (parsed) return parsed;
  if (Math.abs(width - height) <= TABLE_LAYOUT_SHAPE_EPSILON) return "SQUARE";
  return "RECTANGLE";
}

/**
 * Default footprint from seat count (size only, not shape).
 * Squares match {@link TABLE_LAYOUT_BASE_DIMENSION}; rectangles use that same height and wider width for more seats.
 */
export function defaultTableFootprint(tableSize: number): { width: number; height: number } {
  const base = TABLE_LAYOUT_BASE_DIMENSION;
  const n = Number.isFinite(tableSize) && tableSize > 0 ? Math.floor(tableSize) : 4;
  if (n <= 4) return { width: base, height: base };
  if (n <= 6) return { width: 124, height: base };
  if (n <= 8) return { width: 140, height: base };
  if (n <= 10) return { width: 156, height: base };
  return { width: 172, height: base };
}

/**
 * Use stored layout w/h when finite and positive; otherwise default footprint per axis (matches desktop TableNode).
 */
export function effectiveLayoutWidthHeight(
  rawWidth: number | null | undefined,
  rawHeight: number | null | undefined,
  tableSize: number
): { width: number; height: number } {
  const def = defaultTableFootprint(tableSize);
  const w =
    rawWidth != null && Number.isFinite(Number(rawWidth)) && Number(rawWidth) > 0 ? Number(rawWidth) : def.width;
  const h =
    rawHeight != null && Number.isFinite(Number(rawHeight)) && Number(rawHeight) > 0 ? Number(rawHeight) : def.height;
  return { width: w, height: h };
}

/**
 * Circle and square: min(w,h) for both sides (same size as each other).
 * Rectangle from a square: height fixed to {@link TABLE_LAYOUT_BASE_DIMENSION} (Table-1 height); width at least 1.4× base.
 * Other rectangles: preserve width/height.
 */
export function normalizeWidthHeightForShape(
  shape: TableLayoutShape,
  width: number,
  height: number
): { width: number; height: number } {
  const w = width;
  const h = height;
  if (shape === "CIRCLE" || shape === "SQUARE") {
    const s = Math.min(w, h);
    return { width: s, height: s };
  }
  if (Math.abs(w - h) <= TABLE_LAYOUT_SHAPE_EPSILON) {
    const s = Math.min(w, h);
    const rectHeight = TABLE_LAYOUT_BASE_DIMENSION;
    const rectWidth = Math.max(TABLE_LAYOUT_BASE_DIMENSION * 1.4, s * 1.4);
    return { width: Math.round(rectWidth), height: rectHeight };
  }
  return { width: w, height: h };
}
